// GltfRenderer — Sprint 2B scaffold.
//
// Loads a glTF / GLB model via PlayCanvas 2.x's container asset type and
// instantiates its render-component hierarchy into the scene. Activated by
// { renderer: 'gltf', modelUrl: '/assets/models/...' } in rooms.json — see
// src/playcanvas/roomRenderer.ts for the dispatch contract. The ?gltf= URL
// override (see rendererOverrides.ts) lets operators smoke-test a freshly
// authored .glb before committing it to rooms.json, mirroring the ?splat=
// pattern.
//
// Surface swap (wired Sprint 2B):
// • Designer marks meshes in Blender as floor/wall/etc. and defines
//   KHR_materials_variants in the GLB. At runtime, the surface-hotspot
//   panel auto-populates from getMaterialVariants() — see App.openSurfacePanel.
// • Picking a swatch calls applyMaterialVariant(name); the renderer
//   re-snapshots materials so subsequent tint composition stays accurate.
//
// Tint hookup: PBR materials use diffuse (not emissive like the panorama),
// so setTint multiplies every loaded material's diffuse colour. The sun
// system + material panel both push tints; App composes them and forwards.
//
// Lighting note: a glTF room rendered without scene lights looks black,
// because StandardMaterial obeys lighting by default. The scaffold drops a
// neutral ambient + a single directional light at apartment scale so the
// model is visible at all. Sprint 2C's real-time sun + shadow upgrade will
// replace these with a sun-driven directional light + IBL probe.

import * as pc from 'playcanvas';
import type { Room, Variant } from '../app/types';
import type { SunState } from './sunSystem';
import type { RoomRenderer } from './roomRenderer';
import { resolveModelUrl } from './rendererOverrides';

/** Default key light intensity at solar noon (elevation 90°). Scaled by
 *  sin(elevation) as the sun arcs down toward the horizon. */
const KEY_LIGHT_NOON_INTENSITY = 1.0;
/** Minimum intensity at night so the model never goes fully black. */
const KEY_LIGHT_NIGHT_INTENSITY = 0.06;
/** Constant sky-side fill so the dark side of the model isn't pitch black. */
const FILL_LIGHT_INTENSITY = 0.35;

export class GltfRenderer implements RoomRenderer {
  private readonly app: pc.Application;
  private modelEntity: pc.Entity | null = null;
  private currentAsset: pc.Asset | null = null;
  private currentUrl: string | null = null;
  private lightingRoot: pc.Entity | null = null;
  /** Sun-driven directional light. Updated each time setSun is called. */
  private keyLight: pc.Entity | null = null;
  /** Latest SunState pushed via setSun, so ensureLighting can apply it
   *  immediately when the rig is built lazily on the first show(). */
  private lastSunState: SunState | null = null;
  /**
   * Optional shadow-receiver plane dropped under autoframed sample assets
   * (?gltf= smoke tests like the Khronos shoe) so the sun shadow has
   * somewhere to land. Parented under app.root, NOT the model, so
   * snapshotMaterials() doesn't pick up the floor and tint it. Released in
   * releaseCurrent + destroy.
   */
  private smokeTestGround: pc.Entity | null = null;
  /**
   * Cached per-material PBR snapshot so resetTint restores exactly and
   * setTint composes from the authored baseline. Captures diffuse,
   * emissive, and the emissiveMap reference — the last one matters because
   * tint composition optionally routes the emissive boost through the
   * diffuse texture (so the tonal glow stays shape-bound on metallic
   * surfaces where the diffuse channel doesn't contribute).
   */
  private originalMaterials = new Map<
    pc.StandardMaterial,
    { diffuse: pc.Color; emissive: pc.Color; emissiveMap: pc.Texture | null }
  >();

  constructor(app: pc.Application) {
    this.app = app;
  }

  async show(room: Room, variant: Variant): Promise<void> {
    const url = resolveModelUrl(room, variant);
    if (!url) {
      throw new Error(
        `[gltf-renderer] no modelUrl on room "${room.id}" or variant "${variant.id}". ` +
        `Either set room.modelUrl, set variant.modelUrl, pass ?gltf=<url>, ` +
        `or change the renderer back to "panorama".`,
      );
    }

    if (this.modelEntity && this.currentUrl !== url) {
      this.releaseCurrent();
    }

    if (!this.modelEntity) {
      const asset = new pc.Asset(`gltf:${room.id}:${variant.id}`, 'container', { url });
      this.app.assets.add(asset);
      await new Promise<void>((resolve, reject) => {
        const onLoad = (): void => {
          asset.off('error', onError);
          resolve();
        };
        const onError = (err: unknown): void => {
          asset.off('load', onLoad);
          reject(new Error(`[gltf-renderer] failed to load ${url}: ${String(err)}`));
        };
        asset.once('load', onLoad);
        asset.once('error', onError);
        this.app.assets.load(asset);
      });

      const resource = asset.resource as { instantiateRenderEntity?: (opts?: object) => pc.Entity } | null;
      if (!resource || typeof resource.instantiateRenderEntity !== 'function') {
        throw new Error(`[gltf-renderer] loaded asset has no instantiateRenderEntity (${url})`);
      }
      // castShadows + receiveShadows propagate to every meshInstance the
      // loader creates — without these flags glTF imports don't cast onto
      // the sun-driven shadow map regardless of the light's settings.
      const entity = resource.instantiateRenderEntity({ castShadows: true, receiveShadows: true });
      this.app.root.addChild(entity);

      this.modelEntity = entity;
      this.currentAsset = asset;
      this.currentUrl = url;
      this.autoFrameIfSmall(entity);
      this.snapshotMaterials(entity);
    }

    this.ensureLighting();
    this.modelEntity.enabled = true;
    if (this.lightingRoot) this.lightingRoot.enabled = true;
  }

  hide(): void {
    if (this.modelEntity) this.modelEntity.enabled = false;
    if (this.lightingRoot) this.lightingRoot.enabled = false;
  }

  setTint(r: number, g: number, b: number): void {
    // Dual-channel tint: multiplies diffuse AND additively shifts emissive.
    //
    // Why both: glTF PBR materials with metallicFactor=1 (common on Khronos
    // sample assets and any plastic/leather/metal authored generously) route
    // baseColor through specular F0, not through the diffuse term — so a
    // diffuse-only tint is invisible on those surfaces without an HDRI.
    // Adding the deviation from neutral (tint - 1) to emissive guarantees a
    // visible warming/cooling shift everywhere; the emissive boost is
    // routed through the diffuse texture (via emissiveMap) so the glow
    // follows the model's surface, not the empty space.
    //
    // Emissive can only ADD light. Cool/darkening tints (any channel < 1)
    // produce no emissive contribution — they rely on the diffuse multiply
    // alone, which works on non-metals. Fully-metallic samples accept that
    // physically-correct darkening isn't possible without an environment.
    const EMISSIVE_STRENGTH = 0.5;
    const isShifting = r > 1 || g > 1 || b > 1;
    for (const [mat, snap] of this.originalMaterials) {
      mat.diffuse.set(
        Math.max(0, snap.diffuse.r * r),
        Math.max(0, snap.diffuse.g * g),
        Math.max(0, snap.diffuse.b * b),
      );
      mat.emissive.set(
        snap.emissive.r + Math.max(0, r - 1) * EMISSIVE_STRENGTH,
        snap.emissive.g + Math.max(0, g - 1) * EMISSIVE_STRENGTH,
        snap.emissive.b + Math.max(0, b - 1) * EMISSIVE_STRENGTH,
      );
      // Use the diffuse texture as the emissive multiplier only while a
      // warming tint is active and the material has no authored emissive
      // map of its own. Reset path restores the original (often null) map.
      if (isShifting && !snap.emissiveMap && mat.diffuseMap) {
        mat.emissiveMap = mat.diffuseMap;
      } else {
        mat.emissiveMap = snap.emissiveMap;
      }
      mat.update();
    }
  }

  resetTint(): void {
    this.setTint(1, 1, 1);
  }

  /**
   * Reorient + recolor the sun-driven key light from SunSystem state. The
   * key light's local euler maps elevation→pitch (so a high sun shines
   * down, a low sun shines sideways) and azimuth→yaw. Color shifts warm at
   * golden hour and cool dim at night; intensity follows sin(elevation)
   * with a small night floor so the model never goes fully black.
   *
   * Called from App.handleSunChange so the time-of-day slider relights
   * loaded glTF models the same way it tints the panorama.
   */
  setSun(state: SunState): void {
    this.lastSunState = state;
    if (!this.keyLight) return;
    applySunToLight(this.keyLight, state);
    // Save shadow-map render cost when the sun is below the horizon — the
    // key light is already clamped to a 0.06 intensity floor at night, so a
    // shadow at that intensity is invisible anyway.
    const lc = this.keyLight.light;
    if (lc) lc.castShadows = state.elevationDeg >= 0;
  }

  /**
   * Returns the names of any KHR_materials_variants defined in the loaded
   * GLB. Empty array if the model doesn't declare variants. Sprint 2B
   * follow-up will use this to auto-populate surface hotspot swatches.
   */
  getMaterialVariants(): string[] {
    const r = this.currentAsset?.resource as { getMaterialVariants?: () => string[] } | null;
    if (!r || typeof r.getMaterialVariants !== 'function') return [];
    return r.getMaterialVariants();
  }

  /**
   * Apply a named KHR_materials_variants to the currently loaded model.
   * Used by the Sprint 2B surface-hotspot swatch upgrade.
   */
  applyMaterialVariant(name: string | null): void {
    if (!this.modelEntity || !this.currentAsset) return;
    const r = this.currentAsset.resource as {
      applyMaterialVariant?: (entity: pc.Entity, name?: string | null) => void;
    } | null;
    if (!r || typeof r.applyMaterialVariant !== 'function') return;
    r.applyMaterialVariant(this.modelEntity, name ?? undefined);
    // Variant swap rebuilds material refs; re-snapshot so tint reset is accurate.
    this.snapshotMaterials(this.modelEntity);
  }

  destroy(): void {
    this.releaseCurrent();
    if (this.lightingRoot) {
      this.lightingRoot.destroy();
      this.lightingRoot = null;
    }
    this.keyLight = null;
    this.lastSunState = null;
  }

  // ── Internals ─────────────────────────────────────────────────

  /**
   * Smoke-test ergonomics: when a loaded model's max half-extent is under
   * 2 m it's clearly a sample asset (e.g. MaterialsVariantsShoe ~25 cm or
   * DamagedHelmet ~1.8 m diameter) being dropped in via ?gltf= to test the
   * pipeline, not an apartment-scale room. Scale it so its longest side
   * spans ~3 m and translate it ~5 m in front of the default camera so the
   * operator sees the model framed instead of standing inside it.
   * Room-scale models (≥2 m half-extent — i.e. ≥4 m across) are left at
   * their authored coordinates because the camera is meant to sit inside.
   */
  private autoFrameIfSmall(entity: pc.Entity): void {
    const renders = entity.findComponents('render') as pc.RenderComponent[];
    let aabb: pc.BoundingBox | null = null;
    for (const rc of renders) {
      for (const mi of rc.meshInstances) {
        if (!mi.aabb) continue;
        if (!aabb) {
          aabb = new pc.BoundingBox();
          aabb.copy(mi.aabb);
        } else {
          aabb.add(mi.aabb);
        }
      }
    }
    if (!aabb) return;
    const half = aabb.halfExtents;
    const maxHalf = Math.max(half.x, half.y, half.z);
    if (maxHalf >= 2 || maxHalf <= 0) return;

    // Sized + distanced so the whole model fits in the viewport with
    // generous margin (~25% horizontal width at 72° FOV). Far enough that
    // any aabb-centre offset can't push part of the model behind the near
    // plane, close enough that PBR + variant texture detail still reads.
    const TARGET_MAX_EXTENT = 4;
    const VIEW_DISTANCE = 9;
    const scale = TARGET_MAX_EXTENT / (maxHalf * 2);
    entity.setLocalScale(scale, scale, scale);
    // PlayCanvas camera default-looks down -Z, so place the model in front
    // of the camera and re-centre so its scaled centre sits at the target.
    const centre = aabb.center;
    const localY = -centre.y * scale;
    entity.setLocalPosition(
      -centre.x * scale,
      localY,
      -VIEW_DISTANCE - centre.z * scale,
    );

    // Drop a neutral ground plane right under the model so the sun shadow
    // has somewhere to land. Without this the smoke-test shoe/helmet would
    // cast into the void and operators couldn't visually verify the 2C
    // shadow upgrade. Real apartment-scale rooms (handled by the early
    // return above) bring their own floor mesh.
    //
    // Computing the floor Y from the input AABB pre-transform doesn't
    // work reliably: glTF assets often have node-hierarchy transforms
    // baked into their meshes, so the entity-level world transform doesn't
    // capture every offset. Instead, sync the hierarchy and aggregate the
    // *true* world-space bbox per mesh-instance using each node's world
    // matrix. This produces a correct floor Y regardless of authored pivot.
    entity.syncHierarchy();
    const FLOOR_CLEARANCE = 0.1;
    const worldMin = computeWorldMinY(renders);
    if (worldMin !== null) {
      this.spawnSmokeTestGround(worldMin - FLOOR_CLEARANCE);
    }
  }

  /**
   * Build a wide flat ground plane parented to app.root (NOT to
   * modelEntity, so snapshotMaterials doesn't pick up its diffuse and tint
   * the floor as part of the room tone). Receives shadows; never casts.
   * Replaced rather than mutated each show() since the asset can change
   * between calls (different ?gltf= URL on hot-reload). Sized large enough
   * that low-angle sun shadows (early morning / late afternoon) still land
   * on the plane rather than disappearing off the edge.
   */
  private spawnSmokeTestGround(worldY: number): void {
    if (this.smokeTestGround) {
      this.smokeTestGround.destroy();
      this.smokeTestGround = null;
    }
    const ground = new pc.Entity('gltf-smoke-test-ground');
    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(0.35, 0.35, 0.38);
    mat.useLighting = true;
    mat.update();
    ground.addComponent('render', { type: 'plane', material: mat });
    ground.setLocalScale(24, 1, 24);
    ground.setLocalPosition(0, worldY, -9);
    if (ground.render) {
      for (const mi of ground.render.meshInstances) {
        mi.castShadow = false;
        mi.receiveShadow = true;
      }
    }
    this.app.root.addChild(ground);
    this.smokeTestGround = ground;
  }

  private releaseCurrent(): void {
    if (this.modelEntity) {
      this.modelEntity.destroy();
      this.modelEntity = null;
    }
    if (this.currentAsset) {
      this.app.assets.remove(this.currentAsset);
      this.currentAsset = null;
    }
    if (this.smokeTestGround) {
      this.smokeTestGround.destroy();
      this.smokeTestGround = null;
    }
    this.currentUrl = null;
    this.originalMaterials.clear();
  }

  /**
   * Walk every render component in the loaded model and remember each
   * material's authored PBR state (diffuse, emissive, emissiveMap). Used
   * by setTint so repeat tint calls compose from the same baseline, and
   * by resetTint to restore exactly. Re-runs after applyMaterialVariant
   * so variant swaps refresh the baseline before the next tint composes.
   */
  private snapshotMaterials(root: pc.Entity): void {
    this.originalMaterials.clear();
    const renders = root.findComponents('render') as pc.RenderComponent[];
    for (const render of renders) {
      for (const mi of render.meshInstances) {
        const mat = mi.material as pc.StandardMaterial | null;
        if (!mat || !(mat as { diffuse?: pc.Color }).diffuse) continue;
        if (this.originalMaterials.has(mat)) continue;
        this.originalMaterials.set(mat, {
          diffuse: mat.diffuse.clone(),
          emissive: (mat.emissive ?? new pc.Color(0, 0, 0)).clone(),
          emissiveMap: (mat.emissiveMap as pc.Texture | null) ?? null,
        });
      }
    }
  }

  /**
   * Build a minimal lighting rig once. A glTF model rendered without lights
   * is black because StandardMaterial obeys lighting. The rig is a separate
   * entity so destroy() can drop it cleanly. The key light is driven by
   * SunSystem via setSun(); the fill light stays constant (sky-side blue)
   * so the dark side of the model never goes fully black.
   */
  private ensureLighting(): void {
    if (this.lightingRoot) return;
    const root = new pc.Entity('gltf-lighting');

    const key = new pc.Entity('gltf-key-light');
    key.addComponent('light', {
      type: 'directional',
      color: new pc.Color(1, 0.97, 0.9),
      intensity: KEY_LIGHT_NOON_INTENSITY,
      castShadows: true,
      // Quest 3-friendly budget: single 1024² cascade, PCF3 filtering. Covers
      // apartment-scale rooms (~30 m) with minimal cost. Bumping to 2048 or
      // adding a second cascade is the lever if perf headroom appears.
      shadowType: pc.SHADOW_PCF3,
      shadowResolution: 1024,
      shadowDistance: 30,
      numCascades: 1,
      // Bias values tuned to avoid shadow acne on a flat ground plane
      // receiver without producing visible peter-panning on small assets.
      shadowBias: 0.05,
      normalOffsetBias: 0.05,
    });
    // Initial fallback orientation — gets overwritten on the first setSun().
    key.setLocalEulerAngles(50, 30, 0);
    root.addChild(key);
    this.keyLight = key;

    const fill = new pc.Entity('gltf-fill-light');
    fill.addComponent('light', {
      type: 'directional',
      color: new pc.Color(0.85, 0.9, 1),
      intensity: FILL_LIGHT_INTENSITY,
      castShadows: false,
    });
    fill.setLocalEulerAngles(-20, -150, 0);
    root.addChild(fill);

    this.app.root.addChild(root);
    this.lightingRoot = root;

    // Apply any sun state pushed before the rig existed (e.g. SunSystem
    // emits its initial state at App.start before any glTF room has loaded).
    if (this.lastSunState) applySunToLight(key, this.lastSunState);
  }
}

/**
 * Pure helper: map a SunState onto a PlayCanvas directional light entity.
 * Free function (not a method) so it can apply during ensureLighting's
 * lazy startup AND during live setSun calls without a second branch.
 *
 * - Pitch (X-euler) = elevation, so the sun shines down from above at noon
 *   and sideways near the horizon at dusk.
 * - Yaw (Y-euler) = azimuth — geographic 0° = north, increasing clockwise.
 *   Maps to the scene's +Y rotation; for a smoke-test asset placed at the
 *   default -Z this is sufficient to see the light arc as the slider moves.
 * - Color + intensity follow elevation: white-warm at noon, golden at low
 *   sun, cool-dim at night with a small floor so the model stays visible.
 */
/**
 * Aggregate the world-space minimum Y across every meshInstance under the
 * given render components, using each mesh's authored local AABB combined
 * with its node's actual world transform. This is the post-positioning
 * equivalent of the autoFrame-time AABB read, but reliable for glTFs whose
 * meshes have node-hierarchy offsets the root-entity world transform
 * alone doesn't capture. Returns null when no AABBs are available.
 */
function computeWorldMinY(renders: pc.RenderComponent[]): number | null {
  let minY: number | null = null;
  const tmp = new pc.BoundingBox();
  for (const rc of renders) {
    for (const mi of rc.meshInstances) {
      const meshAabb = mi.mesh?.aabb;
      if (!meshAabb) continue;
      tmp.setFromTransformedAabb(meshAabb, mi.node.getWorldTransform());
      const y = tmp.getMin().y;
      if (minY === null || y < minY) minY = y;
    }
  }
  return minY;
}

function applySunToLight(light: pc.Entity, state: SunState): void {
  light.setLocalEulerAngles(state.elevationDeg, state.azimuthDeg, 0);
  const { color, intensity } = sunLightFor(state.elevationDeg);
  const lc = light.light;
  if (!lc) return;
  lc.color = color;
  lc.intensity = intensity;
}

/** Sun colour + intensity curve. Elevation in degrees. */
function sunLightFor(elevationDeg: number): { color: pc.Color; intensity: number } {
  // Three regimes, smoothly interpolated:
  //   night  (-90° .. -6°)   → cool blue, dim floor
  //   dawn/dusk (-6° .. 15°) → orange/golden
  //   day   (15° .. 90°)     → warm-white, scaling with sin(elevation)
  if (elevationDeg <= -6) {
    return { color: new pc.Color(0.3, 0.35, 0.5), intensity: KEY_LIGHT_NIGHT_INTENSITY };
  }
  if (elevationDeg < 15) {
    // Linear blend from horizon orange to mid-morning warm-white.
    const t = clamp((elevationDeg + 6) / 21, 0, 1);
    const r = lerp(1.0, 1.0, t);
    const g = lerp(0.55, 0.92, t);
    const b = lerp(0.25, 0.78, t);
    const intensity = lerp(0.3, 0.7, t);
    return { color: new pc.Color(r, g, b), intensity };
  }
  const sin = Math.sin((elevationDeg * Math.PI) / 180);
  const intensity = lerp(0.7, KEY_LIGHT_NOON_INTENSITY, clamp(sin, 0, 1));
  return { color: new pc.Color(1.0, 0.97, 0.92), intensity };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
